import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

export default function RoleplayScenarios() {
  const canonical = 'https://earnmyedge.com/sales-roleplay-scenarios';
  const ld = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Sales Roleplay Scenarios',
    url: canonical,
  };

  return (
    <main className="mx-auto max-w-4xl px-6 py-12">
      <Helmet>
        <title>Sales Roleplay Scenarios | Earn My Edge</title>
        <meta name="description" content="Practice realistic sales scenarios with AI roleplay and instant feedback to improve discovery, objection handling, and closing." />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Sales Roleplay Scenarios | Earn My Edge" />
        <meta property="og:description" content="Practice realistic sales scenarios with AI roleplay and instant feedback to improve discovery, objection handling, and closing." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="/assets/social-card.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Sales Roleplay Scenarios | Earn My Edge" />
        <meta name="twitter:description" content="Practice realistic sales scenarios with AI roleplay and instant feedback to improve discovery, objection handling, and closing." />
        <meta name="twitter:image" content="/assets/social-card.png" />
        <script type="application/ld+json">{JSON.stringify(ld)}</script>
      </Helmet>

      <header className="mb-8">
        <h1 className="text-3xl font-semibold">Sales Roleplay Scenarios</h1>
        <p className="mt-4 text-muted-foreground">
          Build fluency across discovery, demos, and closing with structured roleplay and coaching.
        </p>
      </header>

      <section className="space-y-6">
        {[
          { title: 'Early Discovery', desc: 'Uncover pain, quantify impact, and build urgency.' },
          { title: 'Budget Pushback', desc: 'Handle pricing concerns while maintaining value.' },
          { title: 'Timeline Stall', desc: 'Move deals forward with mutual action plans.' },
          { title: 'Competitive Comparison', desc: 'Differentiate without disparaging competitors.' },
          { title: 'Decision-Maker Access', desc: 'Secure executive alignment without overstepping.' },
        ].map((s) => (
          <div key={s.title} className="rounded-lg border p-4">
            <h3 className="font-medium">{s.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
            <div className="mt-3">
              <Link to="/app/ai-roleplay" className="text-primary underline">Run this scenario</Link>
            </div>
          </div>
        ))}
      </section>

      <section className="mt-12 pt-6 border-t">
        <h2 className="text-2xl font-medium mb-6">Roleplay Questions</h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-medium">Can I customize scenarios?</h3>
            <p className="mt-1 text-sm text-muted-foreground">Yes. You can add custom objections, prospect personas, and deal contexts to match your real-world situations.</p>
          </div>
          <div>
            <h3 className="font-medium">How realistic are the AI responses?</h3>
            <p className="mt-1 text-sm text-muted-foreground">The AI simulates buyer objections and pushback based on thousands of real sales conversations, providing realistic practice.</p>
          </div>
          <div>
            <h3 className="font-medium">Can I practice multi-turn conversations?</h3>
            <p className="mt-1 text-sm text-muted-foreground">Yes. Run extended roleplay sessions with back-and-forth exchanges to practice handling complex deal dynamics.</p>
          </div>
        </div>
      </section>

      <footer className="mt-10 border-t pt-6">
        <p className="font-medium">Related pages</p>
        <ul className="mt-2 list-disc pl-6">
          <li><Link to="/sales-training" className="underline">Sales Training</Link></li>
          <li><Link to="/ai-sales-coach" className="underline">AI Sales Coach</Link></li>
          <li><Link to="/objection-handling-training" className="underline">Objection Handling Training</Link></li>
          <li><Link to="/sales-frameworks" className="underline">Sales Framework Guides</Link></li>
        </ul>
      </footer>
    </main>
  );
}
